import { SDKCore, SDKEnv, SDKStatusCode } from "@nebula-sdk/compose";
import type {
  SDKInitArgs, SDKInitResult, SDKPlatform, SDKRequest, SDKRequestDeleteOptions, SDKRequestGetOptions, SDKRequestOptions,
  SDKRequestPostOptions, SDKRequestPutOptions, SDKResponseOptions, SDKResult, SDKStorage,
} from "@nebula-sdk/compose";

export class NebulaSiteService extends SDKCore {
  public get support(): boolean {
    return typeof window !== "undefined";
  }

  public get env(): SDKEnv {
    return SDKEnv.Web;
  }

  public get platform(): SDKPlatform {
    if (/android/i.test(window.navigator.userAgent)) return "android" as SDKPlatform;
    if (/ip(?:hone|od|ad)/i.test(window.navigator.userAgent)) return "ios" as SDKPlatform;
    if (/windows/i.test(window.navigator.userAgent)) return "windows" as SDKPlatform;
    if (/macintosh|mac os x/i.test(window.navigator.userAgent)) return "mac" as SDKPlatform;
    return "unknown" as SDKPlatform;
  }

  public readonly localStorage: SDKStorage = {
    getItem: (key) => {
      const value = window.localStorage.getItem(key);
      if (!value) return null;
      return JSON.parse(value) as unknown;
    },
    setItem: (key, value) => {
      window.localStorage.setItem(key, JSON.stringify(value));
    },
    removeItem: (key) => {
      window.localStorage.removeItem(key);
    },
    clear: () => {
      window.localStorage.clear();
    },
  };

  public readonly httpRequest: SDKRequest = {
    get: async <T>(options: SDKRequestGetOptions) => {
      return await this.request<T>({ ...options, method: "GET" });
    },
    post: async <T>(options: SDKRequestPostOptions) => {
      return await this.request<T>({ ...options, method: "POST" });
    },
    put: async <T>(options: SDKRequestPutOptions) => {
      return await this.request<T>({ ...options, method: "PUT" });
    },
    delete: async <T>(options: SDKRequestDeleteOptions) => {
      return await this.request<T>({ ...options, method: "DELETE" });
    },
  };

  public async request<T>(options: SDKRequestOptions): Promise<SDKResponseOptions<T>> {
    return await new Promise<SDKResponseOptions<T>>((resolve) => {
      const origins: Record<string, string> = {
        "https://dev-pay.feiyuapi.com": "https://dev-api.feiyuapi.com",
        "https://sit-pay.feiyuapi.com": "https://sit-api.feiyuapi.com",
        "https://pay.feiyuapi.com": "https://api.feiyuapi.com",
      };

      for (const key in origins) {
        if (!window.location.origin.includes(key)) continue;
        const source = import.meta.env.VITE_PASSPORT_API as string;
        options.url = options.url.replace(source, origins[key]);
      }

      const xhr = new XMLHttpRequest();

      xhr.timeout = options.timeout ?? 60000;

      if (["GET", "DELETE"].includes(options.method)) {
        if (options.data) {
          const query = this.stringifyJson(options.data, { encode: true });
          xhr.open(options.method, `${options.url}?${query}`);
        } else {
          xhr.open(options.method, options.url);
        }
      } else {
        xhr.open(options.method, options.url);
      }

      xhr.setRequestHeader("content-type", "application/json");
      xhr.setRequestHeader("x-host", window.location.host);

      if (options.headers) {
        for (const key in options.headers) {
          xhr.setRequestHeader(key, options.headers[key] as string);
        }
      }

      xhr.onload = () => {
        const data = JSON.parse(xhr.responseText || "{}");

        if (xhr.status !== 200) {
          resolve({ status: "error", code: -1, msg: "error", data: data as T });
          return;
        }

        const internalUrlList: Array<string> = [
          import.meta.env.VITE_GATEWAY_API,
          import.meta.env.VITE_PASSPORT_API,
          import.meta.env.VITE_COMBINE_API,
          import.meta.env.VITE_SERVICE_API,
        ];

        for (const key in origins) {
          if (!window.location.origin.includes(key)) continue;
          internalUrlList.push(origins[key]);
        }

        if (internalUrlList.every((url) => !options.url.includes(url))) {
          resolve({ status: "success", code: 0, msg: "success", data: data as T });
          return;
        }
        resolve({
          key: data.request_id,
          status: data.status === 0 ? "success" : "error",
          code: data.status as number,
          msg: data.message as string,
          data: data.data as T,
        });
      };

      xhr.onerror = () => {
        resolve({ key: -1, status: "error", code: -1, msg: "network error", data: null as T });
      };

      xhr.ontimeout = () => {
        resolve({ key: -1, status: "error", code: -1, msg: "request timeout", data: null as T });
      };

      xhr.send(options.data ? JSON.stringify(options.data) : null);
    });
  }

  // 日志上报
  public async trace(args: {
    eventGroup?: string;
    eventLevel?: "info" | "warn" | "error";
    eventId: string;
    eventName: string;
    eventValue: Record<string, unknown>;
  }): Promise<SDKResult<null>> {
    const navigator = window.navigator as Navigator & {
      connection?: { effectiveType?: string };
      userAgentData?: { platform?: string };
    };

    return await super.track({
      appId: this.options?.config.nbAppId ?? "",
      appVersion: this.version,
      appChannel: this.env,
      userId: this.userInfo?.nbOpenId ?? "",
      userTag: this.userInfo?.openId ?? "",
      deviceId: this.deviceId,
      deviceModel: navigator.userAgentData?.platform ?? "unknown",
      osType: this.platform,
      osVersion: navigator.userAgent,
      networkType: navigator.connection?.effectiveType ?? "unknown",
      eventId: args.eventId,
      eventName: args.eventName,
      eventGroup: args.eventGroup ?? "",
      eventValue: args.eventValue,
      eventTime: this.formatDateTime(Date.now()),
      eventLevel: args.eventLevel ?? "info",
    });
  }

  // 初始化
  public async init(args: SDKInitArgs): Promise<SDKInitResult> {
    if (!this.support) {
      return {
        status: "error",
        code: SDKStatusCode.NoSupport,
        msg: "当前环境无法运行NebulaSDK",
        data: null,
      };
    }

    // 初始化渠道配置
    if (Array.isArray(args.config)) {
      const config = args.config.find((item) => item.nbChannelTemplateCode);
      this.options = config ? { version: args.version, debug: args.debug, config } : null;
    } else {
      this.options = { version: args.version, debug: args.debug, config: args.config };
    }

    // 检查配置是否存在
    if (!this.options) {
      void this.trace({
        eventId: "init",
        eventName: "SDK未找到网页渠道配置",
        eventGroup: "core",
        eventValue: { args, result: "未找到网页渠道配置" },
        eventLevel: "error",
      });
      return { status: "error", code: SDKStatusCode.Error, msg: "未找到网页渠道配置", data: null };
    }

    return await Promise.resolve({
      status: "success",
      code: SDKStatusCode.Success,
      msg: "初始化成功",
      data: this.options.config,
    });
  }
}
